import React, { useEffect, useState } from 'react';

// Components
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

// Utils
import {
  CtaEventDataType,
  CtaType,
  trackCtaClicked,
  trackCtaViewed,
} from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { usePrevious } from '@zola/zola-ui/src/hooks/usePrevious';

// Styles
import { AddedToBookedVendorContainer } from './AddedToBookedVendorTag.styles';

const createTrackingPayload = (): CtaEventDataType => {
  return {
    business_unit: 'CORE',
    cta_id: 'booked-vendor',
    cta_type: CtaType.LINK,
    group_id: 'YOUR_EVENTS',
    position: 0,
  };
};

type AddedToBookedVendorTagProps = {
  /** Displays tag as inline element */
  inline?: boolean;
  /** Whether event has been newly created */
  eventCreated?: boolean;
  /**
   * String - data fetched and event venue has been added to booked vendors
   * Null - data fetched but event venue hasn't been added to booked vendors
   * Undefined - pre-fetch
   */
  referenceVendorUuid?: string | null;
};

/**
 * Tag on an event item indicating whether event venue has been added to booked vendors
 * Three states:
 * 1. Already added to booked vendors - shows tag as inline element on page load
 * 2. Just added to booked vendors - shows tag as block element for newly created or updated events
 * 3. Nothing if venue has not been added to booked vendors
 */
const AddedToBookedVendorTag: React.FC<AddedToBookedVendorTagProps> = ({
  inline = false,
  eventCreated = false,
  referenceVendorUuid,
}) => {
  const previousReferenceVendorUuid = usePrevious(referenceVendorUuid);
  const [justAdded, setJustAdded] = useState(
    Boolean(referenceVendorUuid) && !inline && eventCreated
  );
  const [alreadyAdded, setAlreadyAdded] = useState(
    Boolean(referenceVendorUuid) && inline && !eventCreated
  );

  // Handles updating existing events
  useEffect(() => {
    const justAddedToBookedVendors =
      referenceVendorUuid &&
      referenceVendorUuid.length > 0 &&
      previousReferenceVendorUuid === null &&
      !inline;
    const removedFromBookedVendors =
      previousReferenceVendorUuid &&
      previousReferenceVendorUuid.length > 0 &&
      referenceVendorUuid === null;

    if (justAddedToBookedVendors) {
      setJustAdded(true);
      setAlreadyAdded(false);
    } else if (removedFromBookedVendors) {
      setJustAdded(false);
      setAlreadyAdded(false);
    }
  }, [inline, eventCreated, previousReferenceVendorUuid, referenceVendorUuid]);

  useEffect(() => {
    if (alreadyAdded || justAdded) {
      trackCtaViewed(createTrackingPayload());
    }
  }, [alreadyAdded, justAdded]);

  const handleLinkClick = () => {
    trackCtaClicked(createTrackingPayload());
  };

  const renderVendorLink = (copy: string): JSX.Element => (
    <LinkV2
      href="/wedding-vendors/inquiries/booked"
      target="_blank"
      inline
      onClick={handleLinkClick}
    >
      {copy}
    </LinkV2>
  );

  if (justAdded) {
    return (
      <AddedToBookedVendorContainer>
        <span>Venue added to {renderVendorLink('booked vendors')}</span>
      </AddedToBookedVendorContainer>
    );
  }
  if (alreadyAdded) {
    return <span>&#40;{renderVendorLink('view all booked vendors')}&#41;</span>;
  }
  return <></>;
};

export default AddedToBookedVendorTag;
